import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TaskGroup } from '../../types';
import { ChevronRight, ChevronDown, MoreHorizontal, Pencil, Trash2, MessageSquare } from 'lucide-react';

interface GroupHeaderProps {
  group: TaskGroup;
  taskCount: number;
  completedCount: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  /** Virtual / system group: no rename or menu actions */
  readOnly?: boolean;
  /** Open the consolidated group comments popup (all comments for this group across phases). */
  onOpenComments?: () => void;
  /** Show the unread indicator on the group comments button. */
  hasUnreadComments?: boolean;
  /** Supplier hub: large labeled comments control instead of icon-only. */
  prominentComments?: boolean;
}

const GroupHeader: React.FC<GroupHeaderProps> = ({
  group, taskCount, completedCount, collapsed, onToggleCollapse, onRename, onDelete,
  readOnly = false,
  onOpenComments, hasUnreadComments = false,
  prominentComments = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openMenu = () => {
    const rect = menuBtnRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setShowMenu((open) => !open);
  };

  useEffect(() => {
    if (!showMenu) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuBtnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setShowMenu(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowMenu(false);
    };
    const close = () => setShowMenu(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [showMenu]);

  const handleDelete = () => {
    setShowMenu(false);
    if (
      taskCount > 0
      && !window.confirm(`Delete “${group.name}”? Its ${taskCount} task${taskCount === 1 ? '' : 's'} will stay on the project as ungrouped.`)
    ) {
      return;
    }
    onDelete();
  };

  const handleSave = () => {
    if (name.trim() && name.trim() !== group.name) {
      onRename(name.trim());
    } else {
      setName(group.name);
    }
    setEditing(false);
  };

  return (
    <div
      className={`pm-group-header${showMenu ? ' pm-group-header--menu-open' : ''}`}
      style={{ '--group-color': group.color } as React.CSSProperties}
    >
      <div className="pm-group-color-strip" style={{ background: group.color }} />
      <button className="pm-group-toggle" onClick={onToggleCollapse}>
        {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
      </button>
      {editing ? (
        <input
          className="pm-inline-input pm-group-name-input"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={handleSave}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setName(group.name); setEditing(false); } }}
          autoFocus
        />
      ) : (
        <span className="pm-group-name" onDoubleClick={() => { if (!readOnly) setEditing(true); }}>
          {group.name}
        </span>
      )}
      <span className="pm-group-count">{completedCount}/{taskCount}</span>
      {taskCount > 0 && (
        <div className="pm-group-progress">
          <div className="pm-group-progress-fill" style={{ width: `${taskCount > 0 ? (completedCount / taskCount) * 100 : 0}%` }} />
        </div>
      )}

      {onOpenComments && (
        <button
          type="button"
          data-tour="group-comments-btn"
          className={[
            'pm-group-comments-btn',
            hasUnreadComments ? 'pm-group-comments-btn--unread' : '',
            prominentComments ? 'pm-group-comments-btn--prominent' : '',
          ].filter(Boolean).join(' ')}
          onClick={(e) => { e.stopPropagation(); onOpenComments(); }}
          title={hasUnreadComments ? 'New comments — open group comments' : 'Open group comments'}
          aria-label={hasUnreadComments ? 'Open group comments — has unread' : 'Open group comments'}
        >
          <MessageSquare size={prominentComments ? 18 : 14} strokeWidth={1.85} aria-hidden />
          {prominentComments ? (
            <span className="pm-group-comments-btn-label">
              {hasUnreadComments ? 'New comments' : 'Comments'}
            </span>
          ) : null}
          {hasUnreadComments && <span className="pm-group-comments-dot" aria-hidden />}
        </button>
      )}

      {!readOnly && (
        <div className="pm-group-right">
          <button
            ref={menuBtnRef}
            type="button"
            className="pm-btn-ghost pm-btn-sm pm-group-menu-btn"
            aria-label={`Group actions for ${group.name}`}
            aria-expanded={showMenu}
            onClick={openMenu}
          >
            <MoreHorizontal size={14} />
          </button>
          {showMenu && menuPos && createPortal(
            <div
              ref={menuRef}
              className="pm-dropdown pm-group-menu-dropdown"
              style={{ top: menuPos.top, right: menuPos.right, left: 'auto', width: 'max-content' }}
              role="menu"
            >
              <button type="button" className="pm-dropdown-item" role="menuitem" onClick={() => { setEditing(true); setShowMenu(false); }}>
                <Pencil size={12} /> Rename
              </button>
              <button type="button" className="pm-dropdown-item pm-dropdown-item--danger" role="menuitem" onClick={handleDelete}>
                <Trash2 size={12} /> Delete Group
              </button>
            </div>,
            document.body,
          )}
        </div>
      )}
    </div>
  );
};

export default GroupHeader;
